import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-datatable/styles.css';
import 'dayjs/locale/it';
import 'dayjs/locale/en';
import './main.css';

import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Center, Loader, MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { ModalsProvider } from '@mantine/modals';

import { useGetCurrentUserQuery } from '@api/auth.api';

import { setCredentials } from '@slices/auth.slice';

import useQueryParams from '@hooks/queryParams/useQueryParams';
import useAuth from '@hooks/useAuth';

import ActivateUser from '@components/ActivateUser';
import Home from '@components/Home';
import LoginForm from '@components/LoginForm';
import PasswordReset from '@components/PasswordReset';
import PasswordResetRequest from '@components/PasswordResetRequest';
import RegisterForm from '@components/registerForm/RegisterForm';

import theme from './theme';
import i18n from './i18n/i18n';

export default function App() {
  // --------------------------------------------------------------------------
  // Elementor fixes
  // --------------------------------------------------------------------------
  useLayoutEffect(() => {
    // Remove default elementor styling
    document.getElementById('hello-elementor-css')?.remove();

    // Remove default elementor styling
    const elementorClass = Array.from(document.body.classList).find((v) =>
      v.startsWith('elementor-kit-'),
    );
    if (elementorClass) {
      document.body.classList.remove(elementorClass);
    }

    // Init translations
    // Check URL path to determine language
    const pathname = window.location.pathname;
    const currentLanguage = pathname.startsWith('/en/') ? 'en' : 'it';

    i18n.changeLanguage(currentLanguage);
  }, []);

  // --------------------------------------------------------------------------
  // Routing
  // --------------------------------------------------------------------------
  const [params, setParams] = useQueryParams();
  const page = useMemo(() => params.get('p'), [params]);

  // --------------------------------------------------------------------------
  // Authentication
  // --------------------------------------------------------------------------
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const { isAuthenticated } = useAuth();
  const { data: user, isLoading: isLoadingGetUser } = useGetCurrentUserQuery();

  // Get user when page is reloaded
  useEffect(() => {
    if (!isLoadingGetUser) {
      if (user) {
        dispatch(setCredentials({ user }));
      }

      setIsLoading(false);
    }
  }, [dispatch, user, isLoadingGetUser]);

  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------
  return (
    <MantineProvider theme={theme} forceColorScheme="light">
      <DatesProvider settings={{ locale: i18n.language }}>
        <ModalsProvider
          modalProps={{
            styles: { title: { fontSize: '1.2rem', fontWeight: 'bold' } },
          }}
        >
          {isLoading ? (
            <Center py="10rem">
              <Loader />
            </Center>
          ) : isAuthenticated ? (
            <Home />
          ) : page === 'activate-account' ? (
            <ActivateUser
              token={params.get('t') || ''}
              onSuccess={() => {
                params.delete('p');
                setParams(params);
              }}
            />
          ) : page === 'password-reset-request' ? (
            <PasswordResetRequest />
          ) : page === 'reset-password' ? (
            <PasswordReset />
          ) : page === 'login' ? (
            <LoginForm />
          ) : (
            <RegisterForm />
          )}
        </ModalsProvider>
      </DatesProvider>
    </MantineProvider>
  );
}
